import { apiWithTimeout } from "../../shared/api/timeout";
import { apiHost } from "../../shared/api/helpers";

export interface ImportShop {
  id: string;
  name: string;
  market_site: string;
}

export interface ImportRow {
  row_number: number;
  group_name: string;
  shop_name: string;
  expected_market_site: string;
  shop_id: string;
  shop_item_id: string;
  shop_item_gid: string;
  name: string;
  price_default: number;
  adjust_value_min: number;
  compare_url: string;
  compare_site: string;
  compare_site_fallback: boolean;
  try_assign_my_compare_item: boolean;
  compare_range: "items_all" | "items_same_market" | "items_specified";
  rank_price_basis: "price_exposed" | "price";
  gap_price_basis: "price_exposed" | "price";
  target_rank: number;
  desired_gap: number;
  errors: Record<string, string[]>;
  duplicate: boolean;
  db_duplicate: boolean;
  over_limit: boolean;
  completed?: boolean;
  selected?: boolean;
  create_error?: string;
  lookup_error?: string;
  check_error?: string;
  needs_check?: boolean;
}

export interface ImportPreview {
  rows: ImportRow[];
  shops: ImportShop[];
  summary: {
    total: number;
    valid: number;
    gid_needed: number;
    duplicates: number;
    over_limit: number;
    invalid: number;
    zero_default_price: number;
    max_item_count: number;
    registered_item_count: number;
    remaining: number | null;
  };
}

export async function previewItems(file: File): Promise<ImportPreview> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiWithTimeout.long.post(`${apiHost}/api/items/import/preview/`, form);
  return response.data;
}

export async function downloadItemImportTemplate(): Promise<Blob> {
  const response = await apiWithTimeout.normal.get(`${apiHost}/api/items/import/template/`, {
    responseType: "blob",
  });
  return response.data;
}

export async function checkImportDuplicate(row: ImportRow): Promise<boolean> {
  const response = await apiWithTimeout.normal.post(`${apiHost}/api/items/import/check-row/`, {
    shop_id: row.shop_id,
    shop_item_id: row.shop_item_id,
    compare_url: row.compare_url,
  });
  return Boolean(response.data.duplicate);
}

export async function createImportRow(row: ImportRow): Promise<string> {
  const response = await apiWithTimeout.normal.post(`${apiHost}/api/items/import/rows/`, row);
  return String(response.data.id);
}

export async function lookupImportGid(row: ImportRow): Promise<string> {
  if (row.expected_market_site === "smartstore") {
    const response = await apiWithTimeout.normal.post(
      `${apiHost}/api/shops/${row.shop_id}/origin-product-id`,
      { channel_product_id: row.shop_item_id },
    );
    if (String(response.data.channel_product_id) !== row.shop_item_id) return "";
    return String(response.data.origin_product_id || "");
  }
  const response = await apiWithTimeout.normal.post(`${apiHost}/api/shops/${row.shop_id}/search`, {
    search_keyword: row.shop_item_id,
    page: 1,
  });
  const exact = (response.data.items || []).filter(
    (item: { id: string | number }) => String(item.id) === row.shop_item_id,
  );
  return exact.length === 1 ? String(exact[0].gid || "") : "";
}
